import { useRef, useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useSelector } from 'react-redux';
import styles from './library.module.scss';
import { post } from 'api/externalAccess';
import { get, search, deleteOrRestoreAssessment } from 'api/needsassessments';
import { getCountry } from 'utils/country';
import { setDateFormat } from 'utils/date';
import { downloadPdfReport } from 'utils/pdfReport';
import Header from 'components/common/SecondaryHeader/Header';
import Loading from 'components/common/Loading/Loading';
import ConfirmationModal from 'components/common/ConfirmationModal';
import CopyNeedsAssessment from 'components/common/CopyNeedsAssessment/CopyNeedsAssessment';
import TableSkeleton from './TableSkeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import debounce from 'lodash/debounce';
import { ReactComponent as RestoreIcon } from 'icons/restore.svg';
import { ReactComponent as DeleteIcon } from 'icons/delete.svg';
import { ReactComponent as DownloadIcon } from 'icons/download.svg';
import { ReactComponent as ArrowDown } from 'icons/chevron-down.svg';
import { ReactComponent as DotsMenuIcon } from 'icons/dots.svg';
import { ReactComponent as ShareIcon } from 'icons/share.svg';
import { ReactComponent as CopyIcon } from 'icons/copy.svg';
import { tableHeaders, pageSize } from 'constants/userLibrary';
import { useTranslation } from 'react-i18next';
import { getWorkplaceStyleName } from 'services/inSite/workplaceStyles';
import searchParams from 'constants/searchParams';
import paths from 'constants/paths';
import { openFullPathInNewTab } from 'utils/paths';

export default function Library() {
  const ACTIVE = 'active';
  const { t } = useTranslation();
  const user = useSelector((state) => {
    return state.user;
  });
  const [activeAssessments, setActiveAssessments] = useState([]);
  const [deletedAssessments, setDeletedAssessments] = useState([]);
  const [tabValue, setTabValue] = useState(ACTIVE);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('');
  const [hasNoAssessments, setHasNoAssessments] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [pageNumberActive, setPageNumberActive] = useState(1);
  const [pageNumberDeleted, setPageNumberDeleted] = useState(1);
  const [totalActiveNa, setTotalActiveNa] = useState(0);
  const [totalDeletedNa, setTotalDeletedNa] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const isMountedRef = useRef(true);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const isOwner = (assessment) => {
    return user.email?.toLowerCase() === assessment.ownerEmail?.toLowerCase();
  };
  const isArchiveTab = () => {
    return tabValue !== ACTIVE;
  };

  const handleOpenPopover = (event, asssessment) => {
    setSelectedAssessment(asssessment);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setSelectedAssessment({});
    setAnchorEl(null);
  };

  const isDelete = tabValue === ACTIVE;
  const action = isDelete ? t('common.delete') : t('common.restore');
  const confirmationModalTitle = isDelete
    ? t('library.deleteAssessment')
    : t('library.restoreAssessment');
  const clientAssessmentName = `${selectedAssessment.companyName} - ${selectedAssessment.name}`;
  const confirmationModalText = isDelete
    ? `${t('library.confirmationDelete')} ${clientAssessmentName}?`
    : `${t('library.confirmationRestore')} ${clientAssessmentName}?`;

  const pageNumber = useMemo(() => {
    return tabValue === ACTIVE ? pageNumberActive - 1 : pageNumberDeleted - 1;
  }, [tabValue, pageNumberActive, pageNumberDeleted]);

  const totalCount = useMemo(() => {
    return tabValue === ACTIVE ? totalActiveNa : totalDeletedNa;
  }, [tabValue, totalActiveNa, totalDeletedNa]);

  const currentAssessments = useMemo(() => {
    return tabValue === ACTIVE ? activeAssessments : deletedAssessments;
  }, [tabValue, activeAssessments, deletedAssessments]);

  const fetchData = useCallback(
    async (inputValue, orderBy, order) => {
      setIsTableLoading(true);
      const response = await search(
        inputValue,
        orderBy,
        order,
        pageNumberActive,
        pageNumberDeleted,
        pageSize,
      );

      const deleted = response.deleted;
      const active = response.active;

      if (!isMountedRef.current) return;

      setActiveAssessments(active.needsAssessments);
      setDeletedAssessments(deleted.needsAssessments);
      setIsTableLoading(false);
      if (active.needsAssessments.length || deleted.needsAssessments.length) {
        setHasNoAssessments(false);
      }

      setTotalActiveNa(active.totalNA);
      setTotalDeletedNa(deleted.totalNA);

      loading && setLoading(false);
    },
    [pageNumberActive, pageNumberDeleted, loading],
  );

  useEffect(() => {
    fetchData(searchText, orderBy, order);
  }, [pageNumberActive, pageNumberDeleted]);

  const handleChange = (_, newValue) => {
    setTabValue(newValue);
  };

  const debouncedSearch = useRef(
    debounce((value, pageActive, pageDeleted) => {
      if (pageActive !== 1 || pageDeleted !== 1) {
        setPageNumberActive(1);
        setPageNumberDeleted(1);
      } else {
        fetchData(value, orderBy, order);
      }
    }, 300),
  ).current;

  const debouncedSort = useRef(
    debounce((searchText, property, orderDirection) => {
      fetchData(searchText, property, orderDirection);
    }, 300),
  ).current;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      debouncedSearch.cancel();
      debouncedSort.cancel();
    };
  }, [debouncedSearch, debouncedSort]);

  const handleSearch = (event) => {
    setSearchText(event.target.value);
    debouncedSearch(event.target.value, pageNumberActive, pageNumberDeleted);
  };

  const goToAssessment = (id) => {
    if (tabValue === ACTIVE) {
      openFullPathInNewTab(`${paths.step}${paths.steps.yourPeople}/${id}`);
    }
  };

  const onClearInput = () => {
    fetchData('');
    setSearchText('');
  };

  const onSortHandler = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    debouncedSort(searchText, property, isAsc ? 'desc' : 'asc');
  };

  const handleChangePage = (_, val) => {
    tabValue === ACTIVE ? setPageNumberActive(val + 1) : setPageNumberDeleted(val + 1);
  };

  const assessmentAction = () => {
    setShowConfirmationModal(true);
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
    setSelectedAssessment({});
  };

  const onCopyAssessmnent = (createdAssessment) => {
    if (createdAssessment) {
      fetchData(searchText, orderBy, order);
      goToAssessment(createdAssessment.id);
    }

    setShowCopyDialog(false);
    handleClose();
  };

  const onDeleteOrRestoreAssessmnent = async (confirmIgnore) => {
    if (confirmIgnore) {
      await deleteOrRestoreAssessment(selectedAssessment.id, tabValue === ACTIVE);
      fetchData(searchText, orderBy, order);
      closeConfirmationModal();
      if (tabValue === ACTIVE && pageNumberActive > 1 && defineEmptyPage(totalActiveNa)) {
        setPageNumberActive(pageNumberActive - 1);
      } else if (
        tabValue === 'deleted' &&
        pageNumberDeleted > 1 &&
        defineEmptyPage(totalDeletedNa)
      ) {
        setPageNumberDeleted(pageNumberDeleted - 1);
      }
    } else {
      closeConfirmationModal();
    }
    handleClose();
  };

  const defineEmptyPage = (assessments) => {
    return Number.isInteger((assessments - 1) / pageSize);
  };

  const downloadPdf = async () => {
    setIsDownloading(true);
    const assessment = await get(selectedAssessment.id);
    await downloadPdfReport({
      id: assessment.id,
      name: assessment.name,
      createdAt: assessment.createdAt,
      workplace: t(getWorkplaceStyleName(assessment.workplaceStyleSelected)),
      terms: t('report.termsOfAgreement'),
    });
    handleClose();
    setIsDownloading(false);
  };

  const shareAssessment = async () => {
    try {
      const response = await post(selectedAssessment.id, user.id);
      if (response?.accessKey) {
        openFullPathInNewTab(
          `${paths.clientConfiguration}/${selectedAssessment.id}?${searchParams.accessKey}=${response.accessKey}`,
        );
      }
    } catch (error) {
      console.error(error);
    }
    handleClose();
  };

  const copyAssessment = () => {
    setShowCopyDialog(true);
  };

  return (
    <div data-testid="Library" className={styles.library_container}>
      <ConfirmationModal
        open={showConfirmationModal}
        onAction={onDeleteOrRestoreAssessmnent}
        action={action}
        title={confirmationModalTitle}
        description={confirmationModalText}
      />
      <Header />
      {loading ? (
        <Loading />
      ) : (
        <main className={styles.main}>
          <Box
            sx={{
              display: 'flex',
              marginBottom: '58px',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <h2 className={styles.library__title}>{t('library.title')}</h2>
            {!hasNoAssessments && (
              <OutlinedInput
                data-testid="SearchInput"
                id="search-input"
                type="text"
                value={searchText}
                autoComplete="off"
                onChange={handleSearch}
                className={styles.library__input}
                placeholder={t('library.searchRecord')}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchOutlinedIcon className={styles.library__input_search} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    {searchText && searchText.length > 0 && (
                      <IconButton onClick={onClearInput} edge="end">
                        <HighlightOffIcon className={styles.library__input_close} />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
              />
            )}
          </Box>
          {!hasNoAssessments ? (
            <div data-testid="LibraryTable">
              <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '32px' }}>
                <Tabs
                  className={styles.library__tabs}
                  value={tabValue}
                  textColor="inherit"
                  onChange={handleChange}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: '#60666E',
                    },
                  }}>
                  <Tab
                    data-testid="ActiveTab"
                    disableRipple
                    value="active"
                    className={styles.library__tab}
                    label={`${t('library.active')} (${totalActiveNa})`}
                  />
                  <Tab
                    data-testid="DeletedTab"
                    disableRipple
                    value="deleted"
                    className={styles.library__tab}
                    label={`${t('library.deleted')} (${totalDeletedNa})`}
                  />
                </Tabs>
              </Box>
              <Table className={styles.library__table} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    {Object.values(tableHeaders).map((header) => {
                      return (
                        <TableCell key={header.sortField}>
                          <TableSortLabel
                            data-testid={`SortLabel-${header.title}`}
                            IconComponent={ArrowDown}
                            active={orderBy === header.sortField}
                            direction={orderBy === header.sortField ? order : 'desc'}
                            onClick={() => onSortHandler(header.sortField)}>
                            {t(`library.${header.title}`)}
                          </TableSortLabel>
                        </TableCell>
                      );
                    })}
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ opacity: isTableLoading ? '0.5' : '1' }}>
                  {currentAssessments.map((assessment) => (
                    <TableRow
                      data-testid={`tableRow-${assessment.id}`}
                      onClick={() => goToAssessment(assessment.id)}
                      className={assessment.id === selectedAssessment.id ? styles.hovered : ''}
                      key={assessment.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>
                        <div className={styles.library__table_content}>
                          {assessment.companyName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.library__table_content}>{assessment.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.library__table_content}>{assessment.ownerEmail}</div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.library__table_content}>
                          {setDateFormat(assessment.updatedAt, getCountry())}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.library__table_content}>
                          {setDateFormat(assessment.createdAt, getCountry())}
                        </div>
                      </TableCell>

                      {(!isArchiveTab() || isOwner(assessment) || user.isAdmin) && (
                        <TableCell
                          onClick={(e) => e.stopPropagation()}
                          sx={{ cursor: isDownloading ? 'not-allowed' : 'auto' }}>
                          <IconButton
                            data-testid={`PopoverBtn-${assessment.id}`}
                            className={styles.library__popover_btn}
                            onClick={(e) => handleOpenPopover(e, assessment)}>
                            <DotsMenuIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                data-testid="Pagination"
                component="div"
                count={totalCount}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[]}
                page={pageNumber}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('library.of')} ${count}`
                }
              />
              <Popover
                data-testid={`LibraryPopover-${selectedAssessment.id}`}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}>
                <div className={styles.library__acions_container}>
                  {tabValue === ACTIVE && (
                    <Fragment>
                      <Button
                        data-testid={`ShareBtn-${selectedAssessment.id}`}
                        className={styles.library__action_btn}
                        size="small"
                        startIcon={<ShareIcon />}
                        onClick={shareAssessment}>
                        {t('library.shareBtn')}
                      </Button>
                      <Button
                        data-testid={`CopyBtn-${selectedAssessment.id}`}
                        className={styles.library__action_btn}
                        size="small"
                        startIcon={<CopyIcon />}
                        onClick={copyAssessment}>
                        {t('library.copyBtn')}
                      </Button>
                      <Button
                        data-testid={`DownloadPdf-${selectedAssessment.id}`}
                        className={styles.library__action_btn}
                        size="small"
                        startIcon={
                          isDownloading ? (
                            <CircularProgress color={'primary'} size={16} />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                        onClick={downloadPdf}>
                        {t('library.downloadBtn')}
                      </Button>
                    </Fragment>
                  )}
                  {(isOwner(selectedAssessment) || user.isAdmin) && (
                    <Button
                      data-testid={`ActionButton-${selectedAssessment.id}`}
                      className={styles.library__action_btn}
                      size="small"
                      startIcon={tabValue === ACTIVE ? <DeleteIcon /> : <RestoreIcon />}
                      onClick={assessmentAction}>
                      {tabValue === ACTIVE ? t('library.deleteBtn') : t('library.restoreBtn')}
                    </Button>
                  )}
                </div>
              </Popover>
              {showCopyDialog && (
                <CopyNeedsAssessment
                  assessment={selectedAssessment}
                  open={showCopyDialog}
                  onClose={onCopyAssessmnent}
                />
              )}
            </div>
          ) : (
            <TableSkeleton data-testid="LibraryTableSkeleton" />
          )}
        </main>
      )}
    </div>
  );
}
